import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowLeft, MapPin, Coffee, Mountain, BookOpen, Users, Globe } from "lucide-react";
import HavnsLogo from "../components/HavnsLogo";
import Footer from "../components/Footer";

const Movement = () => {
  const navigate = useNavigate();

  const resources = [
    {
      icon: Coffee,
      title: "The Solo Starter Guide",
      description: "20+ ideas for meaningful platonic meetups—from coffee conversations to co-working sessions.",
      cta: "Read the guide",
    },
    {
      icon: Mountain,
      title: "Travel Companion Checklist",
      description: "How to find the perfect travel buddy without the romantic complications. Questions to ask, red flags to spot.",
      cta: "Get the checklist",
    },
    {
      icon: BookOpen,
      title: "The Independence Manifesto",
      description: "Essays from our community about choosing independence, finding fulfillment, and building platonic bonds.",
      cta: "Read stories",
    },
  ];

  const stats = [
    { number: "68%", label: "of Indian millennials feel lonely at least once a week" },
    { number: "42%", label: "of singles say they want more friends, not partners" },
    { number: "3x", label: "more likely to travel solo than previous generations" },
  ];

  return (
    <div className="min-h-screen bg-[#FBF9F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FBF9F7]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
          <HavnsLogo className="text-2xl" linkTo="/" />
          
          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/philosophy" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Philosophy
            </Link>
            <Link 
              to="/movement" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Movement
            </Link>
            <Link 
              to="/insights" 
              className="text-[#666] hover:text-[#1A1A1A] text-sm font-medium tracking-wide transition-colors"
            >
              Insights
            </Link>
          </div>

          <Button
            onClick={() => navigate("/")}
            className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-8 py-6 text-sm font-medium"
          >
            Join Havns
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#666] hover:text-[#1A1A1A] mb-12 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </button>

          <div className="relative rounded-[2rem] overflow-hidden mb-16">
            <img
              src="https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=1400&fit=crop"
              alt="Friends exploring together"
              className="w-full aspect-[21/9] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-4">
                The Soloist Movement
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight max-w-3xl">
                Redefining connection—<br />
                <span className="italic">one platonic bond at a time.</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight mb-8">
                India's loneliness epidemic is real.
              </h2>
              <p className="text-xl text-[#666] leading-relaxed mb-8">
                We're more connected than ever, yet lonelier than ever. Dating apps promise 
                connection but often deliver disappointment. Friends get married and drift away. 
                Work consumes everything.
              </p>
              <p className="text-xl text-[#666] leading-relaxed">
                The Soloist Movement is our commitment to changing this—by building spaces 
                for platonic connection that actually works.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-[#FBF9F7] rounded-2xl p-8 text-center">
                  <p className="font-serif text-5xl text-[#1A1A1A] mb-2">{stat.number}</p>
                  <p className="text-[#666]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Taking Action */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Taking Action
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-tight">
              We're not just building an app. We're starting a movement.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8">
              <Globe className="w-10 h-10 text-[#C5B358] mb-6" />
              <h3 className="font-serif text-2xl text-[#1A1A1A] mb-4">Global Journeys</h3>
              <p className="text-[#666] leading-relaxed mb-6">
                Every month, we facilitate hundreds of solo travel connections. From weekend 
                Goa trips to month-long Europe adventures, Soloists are exploring the world together.
              </p>
              <p className="text-[#1A1A1A] font-medium">500+ journeys facilitated</p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <Users className="w-10 h-10 text-[#C5B358] mb-6" />
              <h3 className="font-serif text-2xl text-[#1A1A1A] mb-4">Local Circles</h3>
              <p className="text-[#666] leading-relaxed mb-6">
                Coffee meetups in Mumbai, book clubs in Delhi, hiking groups in Bangalore. 
                Our members are building local communities of like-minded independents.
              </p>
              <p className="text-[#1A1A1A] font-medium">1,000+ meetups hosted</p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
              Resources
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A]">
              Tools for the independent life.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources.map((resource, idx) => (
              <div key={idx} className="bg-[#FBF9F7] rounded-2xl p-8 flex flex-col">
                <resource.icon className="w-10 h-10 text-[#C5B358] mb-6" />
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  {resource.title}
                </h3>
                <p className="text-[#666] leading-relaxed mb-6 flex-1">
                  {resource.description}
                </p>
                <button className="text-[#1A1A1A] font-medium flex items-center gap-2 hover:gap-3 transition-all">
                  {resource.cta}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Image */}
      <section className="py-24 bg-[#FBF9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="aspect-[21/9] rounded-[2rem] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1400&fit=crop"
              alt="Community of soloists"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Join the Movement */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#C5B358] font-medium tracking-widest text-sm uppercase mb-6">
            Join the Movement
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white mb-8 max-w-3xl mx-auto">
            Be part of something bigger than just another app.
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto mb-12">
            Every Soloist who joins strengthens our community. Every journey shared 
            proves that connection doesn't require romance. Every meetup normalizes 
            the beauty of platonic friendship.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-white text-[#1A1A1A] hover:bg-[#F5F5F5] rounded-full px-12 py-7 text-base font-medium inline-flex items-center gap-3"
          >
            Join Havns
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Movement;
